/**
 * NeuContext - Global context for the Neu AI Assistant
 * 
 * Provides a bridge between Neu and all app components.
 * Tracks user activity, manages proactive suggestions,
 * and enables Neu to take actions on the user's behalf.
 */
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NeuContext = createContext(null);

export const useNeu = () => {
  const ctx = useContext(NeuContext);
  if (!ctx) throw new Error('useNeu must be used within NeuProvider');
  return ctx;
};

// Page name mapping
const PAGE_NAMES = {
  '/': 'Home',
  '/playground': 'Playground',
  '/system-messages': 'Instructions',
};

export function NeuProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // ─── User Activity Tracking ────────────────────────────────
  const lastActivityRef = useRef(Date.now());
  const pageEntryRef = useRef(Date.now());
  const [userState, setUserState] = useState('active'); // active | idle | stuck

  // ─── Page Context ──────────────────────────────────────────
  const [pageContext, setPageContext] = useState({});

  // ─── Proactive Suggestions ─────────────────────────────────
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const suggestionCooldowns = useRef({});

  // ─── Action Registry ───────────────────────────────────────
  const actionHandlers = useRef({});

  // ─── Notifications ─────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);

  // ─── Chat Control ──────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // ─── Track page changes ────────────────────────────────────
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      pageEntryRef.current = Date.now();
      setActiveSuggestion(null); // Clear suggestions on page change
      setPageContext({});
    }
  }, [location.pathname]);

  // ─── Track global user activity ────────────────────────────
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle > 60000) setUserState('stuck');
      else if (idle > 20000) setUserState('idle');
      else setUserState('active');
    }, 3000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(interval);
    };
  }, []);

  // ─── Register action handler from components ───────────────
  const registerAction = useCallback((name, handler) => {
    actionHandlers.current[name] = handler;
    return () => { delete actionHandlers.current[name]; };
  }, []);

  // ─── Execute a single action ───────────────────────────────
  const executeAction = useCallback(async (actionType, data = {}) => {
    try {
      switch (actionType) {
        case 'navigate': {
          const path = data.path || data.page;
          const resolvedPath = path.startsWith('/') ? path : `/${path}`;
          navigate(resolvedPath);
          return { success: true, message: `Navigated to ${PAGE_NAMES[resolvedPath] || resolvedPath}` };
        }

        case 'updateSystemMessage': {
          window.dispatchEvent(new CustomEvent('updateSystemMessage', { detail: data }));
          return { success: true, message: 'Updated the system message in the editor' };
        }

        case 'updatePrompt': {
          window.dispatchEvent(new CustomEvent('updateSystemMessage', { detail: { prompt: data.prompt } }));
          return { success: true, message: 'Updated the prompt in the editor' };
        }

        case 'updateJsonSchema': {
          window.dispatchEvent(new CustomEvent('updateSystemMessage', { detail: { jsonSchema: data.jsonSchema } }));
          return { success: true, message: 'Updated the JSON schema in the editor' };
        }

        case 'openChat': {
          setChatOpen(true);
          return { success: true };
        }

        default: {
          // Try registered handlers from components
          const handler = actionHandlers.current[actionType];
          if (handler) {
            return await handler(data);
          }
          return { success: false, message: `Unknown action: ${actionType}` };
        }
      }
    } catch (err) {
      console.error(`Action ${actionType} failed:`, err);
      return { success: false, message: `Action failed: ${err.message}` };
    }
  }, [navigate]);

  // ─── Execute batch of actions from backend ─────────────────
  const executeActions = useCallback(async (actions) => {
    if (!actions || actions.length === 0) return [];
    const results = [];
    for (const action of actions) {
      const result = await executeAction(action.type, action.data || {});
      results.push({ ...action, ...result });
    }
    return results;
  }, [executeAction]);

  // ─── Notifications ─────────────────────────────────────────
  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  // ─── Proactive Suggestions ─────────────────────────────────
  const showSuggestion = useCallback((suggestion) => {
    const key = suggestion.id || suggestion.text;
    const lastShown = suggestionCooldowns.current[key] || 0;
    const cooldown = suggestion.cooldown || 120000; // 2 min default
    if (Date.now() - lastShown < cooldown) return false;

    suggestionCooldowns.current[key] = Date.now();
    setActiveSuggestion(suggestion);
    return true;
  }, []);

  const dismissSuggestion = useCallback(() => {
    setActiveSuggestion(null);
  }, []);

  // ─── Context Value ─────────────────────────────────────────
  const value = {
    // Route
    currentPath: location.pathname,
    currentPageName: PAGE_NAMES[location.pathname] || location.pathname,
    navigate: (path) => navigate(path),

    // User state
    userState,
    lastActivityRef,
    pageEntryRef,
    markActivity: () => { lastActivityRef.current = Date.now(); },

    // Page context (set by individual pages)
    pageContext,
    setPageContext,

    // Proactive
    activeSuggestion,
    showSuggestion,
    dismissSuggestion,

    // Actions
    registerAction,
    executeAction,
    executeActions,

    // Notifications
    notifications,
    addNotification,

    // Chat
    chatOpen,
    setChatOpen,
    pendingAction,
    setPendingAction,
  };

  return <NeuContext.Provider value={value}>{children}</NeuContext.Provider>;
}

export default NeuContext;
